import { useTranslate } from "@tolgee/react";
import { AlertTriangle, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { getHyperliquidErrorMessage } from "#/features/trade/lib/hyperliquid-error";
import { formatSize, toNumber } from "#/lib/formatters";
import { cn } from "#/lib/utils";
import {
	AvailableUsdcCard,
	formatMaxAmount,
	sanitizeAmountInput,
	usePrefillMaxOnce,
} from "../shared-fund-flow";

export function StepTransferToWallet({
	amount,
	isPending,
	readOnly,
	withdrawable,
	onAmountChange,
	onConfirm,
	onSuccess,
	setIsPending,
}: {
	amount: string;
	isPending: boolean;
	readOnly: boolean;
	withdrawable: number;
	onAmountChange: (value: string) => void;
	onConfirm?: (amount: string) => Promise<void>;
	onSuccess: () => void;
	setIsPending: (value: boolean) => void;
}) {
	const { t } = useTranslate();
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (readOnly) setError(null);
	}, [readOnly]);

	const maxAmount = withdrawable > 0 ? formatMaxAmount(withdrawable) : "";
	usePrefillMaxOnce(!readOnly, maxAmount, amount, onAmountChange);

	function handleAmountChange(nextValue: string) {
		onAmountChange(sanitizeAmountInput(nextValue));
		setError(null);
	}

	function handleMax() {
		if (withdrawable <= 0 || readOnly) {
			onAmountChange("");
			return;
		}
		onAmountChange(maxAmount);
		setError(null);
	}

	const enteredAmount = toNumber(amount);
	const exceedsBalance = enteredAmount > withdrawable;
	const belowMinimum = enteredAmount > 0 && enteredAmount < 5;
	const canConfirm =
		!readOnly &&
		enteredAmount >= 5 &&
		withdrawable > 0 &&
		enteredAmount <= withdrawable;

	function handleConfirm() {
		if (!canConfirm || isPending) return;
		setIsPending(true);
		setError(null);
		const confirmation = onConfirm?.(amount) ?? Promise.resolve();

		confirmation
			.then(() => onSuccess())
			.catch((err: unknown) =>
				setError(
					getHyperliquidErrorMessage(t, err, t("transferWallet.failed")),
				),
			)
			.finally(() => setIsPending(false));
	}

	return (
		<div className="flex flex-col gap-3">
			<AvailableUsdcCard
				label={t("transferWallet.available")}
				amount={withdrawable}
			/>

			<div className="flex items-center justify-between gap-3 rounded-[12px] border border-trade-border-subtle bg-trade-surface-inset px-3.5 py-2.5 text-label2 text-trade-text-secondary">
				<span>{t("transferWallet.time-estimate")}</span>
				<span>{t("transferWallet.fee")}</span>
			</div>

			<div className="rounded-[16px] border border-trade-border-subtle bg-trade-surface-inset px-3.5 py-3 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.02)]">
				<div className="flex items-center justify-between gap-3 text-caption1-medium uppercase text-trade-text-faint">
					<span>{t("form.amount")}</span>
					<button
						type="button"
						onClick={handleMax}
						disabled={isPending || readOnly}
						className="inline-flex min-h-6 items-center rounded-xl border border-primary/20 bg-primary/10 px-2 text-caption1-medium text-primary transition-colors hover:bg-primary/16 disabled:opacity-50 cursor-pointer"
					>
						{t("form.max")}
					</button>
				</div>

				<div className="mt-2 flex items-center gap-3">
					<input
						type="text"
						inputMode="decimal"
						value={amount}
						onChange={(event) => handleAmountChange(event.target.value)}
						placeholder="0.0"
						disabled={isPending || readOnly}
						aria-label={t("transferWallet.amount-aria")}
						className="h-10 w-full min-w-0 border-none bg-transparent px-0 text-title1 text-trade-text-strong outline-none placeholder:text-trade-text-faint disabled:opacity-50"
					/>
					<span className="shrink-0 text-label1-bold text-trade-text-faint">
						USDC
					</span>
				</div>

				<div
					className={cn(
						"mt-1 min-h-5 text-right text-caption1-medium",
						exceedsBalance || belowMinimum
							? "text-primary"
							: "text-transparent",
					)}
				>
					{exceedsBalance
						? t("validation.exceeds-available", {
								balance: formatSize(withdrawable, 6),
							})
						: belowMinimum
							? t("withdraw.minimum-amount")
							: "."}
				</div>
			</div>

			{error && (
				<div className="flex items-start gap-2 rounded-[12px] border border-[var(--color-theme-status-negative)]/20 bg-[var(--color-theme-status-negative)]/10 px-3.5 py-2.5 text-label2 text-[var(--color-theme-status-negative)] dark:text-[var(--color-theme-status-negative)]">
					<AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
					<span>{error}</span>
				</div>
			)}

			<button
				type="button"
				onClick={handleConfirm}
				disabled={!canConfirm || isPending}
				className="mt-1 flex min-h-12 w-full items-center justify-center gap-2 rounded-[14px] bg-primary text-body2-bold text-primary-foreground transition-[filter,transform,opacity] hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-45 cursor-pointer"
			>
				{isPending && <Loader2 className="h-4 w-4 animate-spin" />}
				{t("withdraw.title-step1")}
			</button>
		</div>
	);
}
